import { createGlobalStyle } from "styled-components";
import { colors, typography } from "../theme/bubakiiTheme";

/**
 * Global Styles for Bubakii UI
 * Following the design system guidelines
 */
export const GlobalStyles = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  html {
    font-size: 16px;
    scroll-behavior: smooth;
  }
  
  body {
    font-family: ${typography.fontFamily};
    font-weight: ${typography.textWeight};
    font-size: ${typography.sizes.base};
    line-height: 1.6;
    color: ${colors.text};
    background-color: ${colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: ${typography.fontFamily};
    font-weight: ${typography.headingWeight};
    line-height: 1.3;
    margin-bottom: 1rem;
    color: ${colors.text};
  }
  
  h1 {
    font-size: ${typography.sizes["4xl"]};
  }
  
  h2 {
    font-size: ${typography.sizes["3xl"]};
  }
  
  h3 {
    font-size: ${typography.sizes["2xl"]};
  }
  
  h4 {
    font-size: ${typography.sizes.xl};
  }
  
  h5 {
    font-size: ${typography.sizes.lg};
  }
  
  h6 {
    font-size: ${typography.sizes.base};
  }
  
  p {
    margin-bottom: 1rem;
    color: ${colors.text};
  }
  
  a {
    color: ${colors.primary};
    text-decoration: none;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${colors.primaryLight};
    }
  }
  
  button {
    font-family: ${typography.fontFamily};
    cursor: pointer;
    border: none;
    outline: none;
    transition: all 0.2s ease;
  }
  
  input, textarea, select {
    font-family: ${typography.fontFamily};
    font-size: ${typography.sizes.base};
  }

  /* Ant Design controls can override the element rule above; keep ≥16px to avoid iOS zoom */
  .ant-input,
  .ant-input-affix-wrapper > input.ant-input,
  .ant-input-number-input,
  .ant-select-selection-search-input,
  .ant-picker-input > input,
  .ant-input-textarea textarea {
    font-size: ${typography.sizes.base} !important;
  }
  
  /* Scrollbar Styling */
  ::-webkit-scrollbar {
    width: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${colors.gray[100]};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${colors.gray[300]};
    border-radius: 4px;
    
    &:hover {
      background: ${colors.gray[400]};
    }
  }
  
  /* Focus Styles */
  *:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
  
  /* Selection */
  ::selection {
    background-color: ${colors.primary};
    color: ${colors.white};
  }
  
  /* Utility Classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  .text-center {
    text-align: center;
  }
  
  .text-left {
    text-align: left;
  }
  
  .text-right {
    text-align: right;
  }
  
  .mb-0 { margin-bottom: 0; }
  .mb-1 { margin-bottom: 0.25rem; }
  .mb-2 { margin-bottom: 0.5rem; }
  .mb-3 { margin-bottom: 1rem; }
  .mb-4 { margin-bottom: 1.5rem; }
  .mb-5 { margin-bottom: 3rem; }
  
  .mt-0 { margin-top: 0; }
  .mt-1 { margin-top: 0.25rem; }
  .mt-2 { margin-top: 0.5rem; }
  .mt-3 { margin-top: 1rem; }
  .mt-4 { margin-top: 1.5rem; }
  .mt-5 { margin-top: 3rem; }
  
  .p-0 { padding: 0; }
  .p-1 { padding: 0.25rem; }
  .p-2 { padding: 0.5rem; }
  .p-3 { padding: 1rem; }
  .p-4 { padding: 1.5rem; }
  .p-5 { padding: 3rem; }
  
  /* Tooltip Styles */
  .ant-tooltip {
    z-index: 9999 !important;
  }
  
  .ant-tooltip-inner {
    background-color: rgba(0, 0, 0, 0.9) !important;
    color: white !important;
    font-size: 14px !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
  }
  
  .ant-tooltip-arrow::before {
    background-color: rgba(0, 0, 0, 0.9) !important;
  }
  
  /* Negotiation Modal Z-Index Override */
  .negotiation-modal-override {
    z-index: 99999 !important;
  }
  
  .negotiation-modal-override .ant-modal {
    z-index: 99999 !important;
  }
  
  .negotiation-modal-override .ant-modal-mask {
    z-index: 99998 !important;
  }

  /*
   * Ant Design DatePicker / RangePicker mobile safety net.
   * Prefer DateTimeField (native datetime-local on small screens) for
   * date+time inputs. These rules keep remaining Ant popups usable when
   * the calendar+time panel would otherwise overflow the viewport.
   */
  @media (max-width: 959px) {
    .ant-picker-dropdown {
      max-width: calc(100vw - 16px) !important;
    }

    .ant-picker-panel-container {
      max-width: 100%;
    }

    .ant-picker-panels {
      flex-direction: column !important;
    }

    .ant-picker-datetime-panel,
    .ant-picker-date-panel + .ant-picker-time-panel,
    .ant-picker-panel-layout {
      flex-direction: column !important;
      flex-wrap: wrap !important;
    }

    .ant-picker-time-panel {
      width: 100% !important;
      max-width: 100% !important;
      border-inline-start: none !important;
    }

    .ant-picker-time-panel-column {
      flex: 1 1 auto;
      overflow-y: auto;
      max-height: 40vh;
    }
  }
`;
